import React, { useMemo } from 'react';
import Table from '../../../../component/table/Table';
import OdKpiRow from '../components/OdKpiRow';
import OdShareBar from '../components/OdShareBar';
import OdEcoPill from '../components/OdEcoPill';
import th from '../components/odColumnHeader';
import odTableStyles from '../components/odTableStyles';
import {
    buildAssetSummary,
    compactDuration,
    hasEnergyData,
    hasFuelData,
    summariseTrips,
} from '../odAnalyticsUtils';

const num = (value) => <span className="od-cell-num">{value}</span>;

/** An absent measure is an em dash, never a zero — zero would read as "measured, and clean". */
const numOrDash = (value, render) => (
    value === null || value === undefined
        ? <span className="od-cell-muted">—</span>
        : <span className="od-cell-num">{render(value)}</span>
);

const idleTone = (pct) => {
    if (pct > 25) return 'bad';
    if (pct > 15) return 'warn';
    return 'ok';
};

/**
 * Fleet-level summary: KPI tiles over the whole result set, then one row per asset.
 * Clicking a row loads that asset into the Route Map, the way Route Atlas's fleet table
 * loads an asset into every other tab.
 *
 * The column set follows fleetconsole's fleet table, minus two of its columns: `Source` (which
 * export shape a file matched — meaningless here, there is no file) and `Power`, which has no
 * source in the O/D response yet and is deliberately absent rather than a permanent dash.
 */
const OverviewView = (props) => {
    const {
        results, trips, fuelTrips, analytics, selectedAsset, onSelectAsset,
    } = props;

    // fuelTrips is not optional in practice: a CAN asset reports fuel as a percentage, so its litres
    // exist only in the ledger and every figure below would read 0 L without it.
    const rows = useMemo(
        () => buildAssetSummary(results, fuelTrips, analytics),
        [results, fuelTrips, analytics],
    );
    const totals = useMemo(() => summariseTrips(trips, fuelTrips), [trips, fuelTrips]);
    const showFuel = useMemo(() => hasFuelData(trips, fuelTrips), [trips, fuelTrips]);
    const showEnergy = useMemo(() => hasEnergyData(trips), [trips]);

    const assetsWithTrips = rows.filter((row) => row.trips > 0).length;
    const failed = rows.filter((row) => row.error).length;
    const ghostTotal = rows.reduce((total, row) => total + (row.ghostTrips || 0), 0);
    // Folded from the rows so the tile and the column beside it cannot disagree — the rows carry
    // the window-wide idle figure, `totals` only ever sees the trip list.
    const fleetDriveSec = rows.reduce((total, row) => total + row.driveSec, 0);
    const fleetIdleSec = rows.reduce((total, row) => total + row.idleSec, 0);
    const fleetEngineOnSec = fleetDriveSec + fleetIdleSec;
    const idlePct = fleetEngineOnSec > 0 ? (100 * fleetIdleSec) / fleetEngineOnSec : 0;

    const tiles = [
        {
            key: 'assets',
            label: 'Assets',
            value: rows.length,
            sub: failed > 0
                ? `${assetsWithTrips} with trips · ${failed} failed`
                : `${assetsWithTrips} with trips`,
            tone: failed > 0 ? 'bad' : undefined,
        },
        {
            key: 'trips',
            label: 'Trips',
            value: totals.trips,
            sub: ghostTotal > 0 ? `${ghostTotal} ghost excluded` : undefined,
        },
        {
            key: 'distance',
            label: 'Distance',
            value: totals.distanceKm.toLocaleString(),
            unit: 'km',
        },
        {
            key: 'duration',
            label: 'Total duration',
            value: compactDuration(totals.durationSec),
        },
        {
            key: 'idle',
            label: 'Engine-on idle',
            value: compactDuration(fleetIdleSec),
            sub: `${idlePct.toFixed(1)}% of engine-on time`,
            tone: idleTone(idlePct),
        },
        {
            key: 'avgDistance',
            label: 'Avg distance',
            value: totals.avgDistanceKm.toLocaleString(),
            unit: 'km',
        },
        { key: 'avgDuration', label: 'Avg duration', value: compactDuration(totals.avgDurationSec) },
        showFuel && {
            key: 'fuel', label: 'Fuel used', value: totals.fuelLitre.toLocaleString(), unit: 'L',
        },
        showEnergy && {
            key: 'energy', label: 'Energy used', value: totals.energyKwh.toLocaleString(), unit: 'kWh',
        },
    ];

    // Tooltip text is fleetconsole's own `data-tip` for each column, so the two tools explain a
    // figure the same way. Widths are the header label plus 20px of padding and the 18px RDT
    // reserves for the sort arrow even while it is invisible — that reservation, not the data, is
    // what makes a wide table overflow its card.
    const columns = useMemo(() => [
        {
            name: th('Asset', 'Vehicle registration or asset name, taken from the telemetry.'),
            selector: (row) => row.assetName,
            sortable: true,
            grow: 2,
            minWidth: '8.5rem',
            cell: (row) => (
                <div className="od-cell-stack">
                    <span className="od-cell-strong">{row.assetName}</span>
                    {row.error
                        ? <span className="od-badge od-badge-error" title={row.error}>Failed</span>
                        : <span className="od-cell-muted">{row.deviceSerial || '—'}</span>}
                </div>
            ),
        },
        {
            name: th('Trips', 'Completed journeys. A grey +N counts ghost trips — journeys shorter than the vehicle preset’s minimum trip distance, excluded from every other figure in this row.'),
            selector: (row) => row.trips,
            sortable: true,
            right: true,
            minWidth: '6.5rem',
            cell: (row) => (
                <span className="od-cell-num">
                    {row.trips}
                    {row.ghostTrips > 0 && (
                        <span
                          className="od-cell-muted"
                          title={`${row.ghostTrips} ghost trip${row.ghostTrips === 1 ? '' : 's'} — below the minimum trip distance, excluded from every metric`}
                        >
                            {` +${row.ghostTrips} ghost`}
                        </span>
                    )}
                </span>
            ),
        },
        {
            name: th('Distance', 'Total distance driven, in kilometres. Odometer where available, GPS otherwise.'),
            selector: (row) => row.distanceKm,
            sortable: true,
            right: true,
            minWidth: '5.75rem',
            cell: (row) => num(`${row.distanceKm.toLocaleString()} km`),
        },
        {
            name: th('Drive', 'Time inside a trip with the vehicle actually moving — the trip’s duration less its idling. fleetconsole’s column of this name reports whole trip duration instead, so it reads higher.'),
            selector: (row) => row.driveSec,
            sortable: true,
            right: true,
            minWidth: '5rem',
            cell: (row) => num(compactDuration(row.driveSec)),
        },
        {
            name: th('Idle', 'Engine-on time with the vehicle stationary, across the whole window — including idling between trips, which the trip list alone cannot see.'),
            selector: (row) => row.idleSec,
            sortable: true,
            right: true,
            minWidth: '4.5rem',
            cell: (row) => num(compactDuration(row.idleSec)),
        },
        {
            name: th('Idle share', 'Idling as a fraction of engine-on time — the Idle and Drive columns beside it, which are disjoint and sum to engine-on time.'),
            selector: (row) => row.idleSharePct,
            sortable: true,
            grow: 1.4,
            minWidth: '7rem',
            cell: (row) => <OdShareBar idlePct={row.engineOnSec > 0 ? row.idleSharePct : null} />,
        },
        {
            name: th('Excess', 'Idle time beyond this vehicle preset’s excessive-idle limit.'),
            selector: (row) => row.excessSec ?? -1,
            sortable: true,
            right: true,
            minWidth: '5.25rem',
            cell: (row) => numOrDash(row.excessSec, compactDuration),
        },
        {
            name: th('Events', 'Harsh acceleration, braking, cornering and overspeeding events combined.'),
            selector: (row) => row.events ?? -1,
            sortable: true,
            right: true,
            minWidth: '5rem',
            cell: (row) => numOrDash(row.events, (value) => value.toLocaleString()),
        },
        {
            name: th('Events / 100 km', 'Safety events per 100 km — not electric-vehicle anything. Distance-normalised so a high-mileage asset is comparable with a low-mileage one. Blank under a kilometre travelled, where the rate would be noise.'),
            selector: (row) => row.eventsPer100 ?? -1,
            sortable: true,
            right: true,
            minWidth: '7.5rem',
            cell: (row) => numOrDash(row.eventsPer100, (value) => value.toFixed(1)),
        },
        {
            name: th('Eco 0–10', 'Device-reported eco-driving score, distance-weighted so a long trip counts for more than a short one. Higher is better — the opposite direction to every other coloured column here.'),
            selector: (row) => row.ecoScore ?? -1,
            sortable: true,
            right: true,
            minWidth: '6.5rem',
            cell: (row) => <OdEcoPill score={row.ecoScore} />,
        },
        showFuel && {
            name: th('Fuel', 'Litres burnt, with litres per 100 km beside it. ICE and hybrid only.'),
            selector: (row) => row.fuelLitre,
            sortable: true,
            right: true,
            minWidth: '7rem',
            cell: (row) => (
                <span className="od-cell-num">
                    {`${row.fuelLitre.toLocaleString()} L`}
                    {row.litresPer100 !== null && (
                        <span className="od-cell-muted">{` ${row.litresPer100.toFixed(1)}/100`}</span>
                    )}
                </span>
            ),
        },
        showEnergy && {
            name: th('Energy', 'Energy used, with kWh per 100 km beside it. EV and hybrid only.'),
            selector: (row) => row.energyKwh,
            sortable: true,
            right: true,
            minWidth: '8rem',
            cell: (row) => (
                <span className="od-cell-num">
                    {`${row.energyKwh.toLocaleString()} kWh`}
                    {row.kwhPer100 !== null && (
                        <span className="od-cell-muted">{` ${row.kwhPer100.toFixed(1)}/100`}</span>
                    )}
                </span>
            ),
        },
        {
            name: th('Drains', 'Sudden unexplained fuel drops flagged as possible theft.'),
            selector: (row) => row.drains ?? -1,
            sortable: true,
            right: true,
            minWidth: '5.5rem',
            // Coloured figure, no tint behind it — see `.od-count` in the stylesheet.
            cell: (row) => {
                if (!row.drains) return <span className="od-cell-muted">—</span>;
                return (
                    <span className="od-count od-count-error">
                        {`${row.drains} · ${row.drainLitre.toFixed(0)} L`}
                    </span>
                );
            },
        },
    ].filter(Boolean), [showFuel, showEnergy]);

    // A failed asset keeps its row but is tinted so it never reads as "zero trips". The picked
    // asset is tinted too, with a left rule — it is the one the other tabs are showing, and
    // coming back here to check which that is should not mean reading the chip above the tabs.
    const rowStyles = useMemo(() => [
        {
            when: (row) => Boolean(row.error),
            style: { backgroundColor: '#FEF3F2' },
        },
        {
            when: (row) => Boolean(selectedAsset) && row.assetName === selectedAsset,
            style: {
                backgroundColor: '#EAF1F5',
                boxShadow: 'inset 0.1875rem 0 0 #365A6D',
            },
        },
    ], [selectedAsset]);

    return (
        <div className="od-view">
            <OdKpiRow tiles={tiles} />

            <div className="od-card">
                <div className="od-card-header">
                    <div className="od-card-header-text">
                        <div className="od-card-title">Assets</div>
                        <div className="od-card-subtitle">
                            Click a row to open that asset on the Route Map
                        </div>
                    </div>
                </div>
                <div className="od-card-body od-table-wrap">
                    <Table
                      tableData={rows}
                      columns={columns}
                      module="odAnalytics"
                      pagination={rows.length > 10}
                      paginationServer={false}
                      paginationPerPage={10}
                      handlePageChange={() => {}}
                      handlePerRowsChange={() => {}}
                      setSelectedRowCount={() => {}}
                      totalRows={rows.length}
                      customStyles={odTableStyles}
                      rowStyles={rowStyles}
                      // Trips as well as error: an asset with none has nothing to open, and picking
                      // it would set an asset the selector above has no option for.
                      onRowClicked={(row) => !row.error && row.trips > 0 && onSelectAsset(row.id)}
                      highlightOnHover
                      pointerOnHover
                    />
                </div>
                <div className="od-card-note">
                    <b>Drive</b>
                    {' '}
                    is time inside a trip with the vehicle moving;
                    {' '}
                    <b>Idle</b>
                    {' '}
                    is engine-on stationary time across the whole window, so it includes idling
                    between trips that the trip list cannot see. The two are disjoint and sum to
                    engine-on time, which is what Idle share divides by.
                    {' '}
                    <b>Ghost trips</b>
                    {' '}
                    fall below the vehicle preset&apos;s minimum trip distance and are excluded from
                    every figure here. Excess, Events and Drains need the analytics payload; an em
                    dash means it was not loaded, not that the asset was clean.
                </div>
            </div>
        </div>
    );
};

export default OverviewView;
